import { Suspense, lazy, useEffect, useState } from 'react';
import { NavLink, Navigate, Route, Routes, useNavigate } from 'react-router';
import { LocaleProvider, LocaleSwitch, useI18n } from './i18n/context';
import { ApiError, api, type Me } from './lib/api';
import { handleCallback, isLoggedIn, login, logout } from './lib/auth';
import Keys from './pages/Keys';
import Overview from './pages/Overview';

// 用量页依赖 recharts（约 400 KB）。懒加载让它只在用户真的打开这一页时才下载 ——
// 大多数人进控制台是来拿密钥的，不该为一个图表库付首屏成本。
const Usage = lazy(() => import('./pages/Usage'));

function Callback() {
  const { s } = useI18n();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    handleCallback(location.search)
      .then(() => navigate('/', { replace: true }))
      .catch((e) => setError(String(e.message ?? e)));
  }, [navigate]);

  return (
    <div className="center">
      <div>
        {error ? (
          <>
            <p className="banner danger">{error}</p>
            <button className="btn btn-primary" onClick={() => void login()}>
              {s.auth.retryLogin}
            </button>
          </>
        ) : (
          <p style={{ color: 'var(--ink-dim)' }}>{s.auth.completing}</p>
        )}
      </div>
    </div>
  );
}

function SignIn() {
  const { s } = useI18n();
  return (
    <div className="center">
      <div style={{ maxWidth: 380 }}>
        <div className="brand" style={{ justifyContent: 'center', padding: '0 0 18px' }}>
          <span className="mark">S</span>
          <span style={{ fontSize: 19 }}>{s.auth.consoleName}</span>
        </div>
        <p style={{ color: 'var(--ink-soft)', marginBottom: 24 }}>{s.auth.signInBlurb}</p>
        <button className="btn btn-primary" onClick={() => void login()}>
          {s.auth.signIn}
        </button>
        {/* 登录前就要能切语言 —— 看不懂这一屏的人正是最需要它的人。 */}
        <div style={{ marginTop: 22 }}>
          <LocaleSwitch />
        </div>
      </div>
    </div>
  );
}

function Shell() {
  const { s } = useI18n();
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .me()
      .then(setMe)
      .catch((e) => {
        // 登录态失效时把人送回登录页，而不是卡在一个空白界面上。
        if (e instanceof ApiError && e.status === 401) {
          void login();
          return;
        }
        setError(e instanceof ApiError ? e.message : String(e));
      });
  }, []);

  if (error) {
    return (
      <div className="center">
        <div style={{ maxWidth: 460 }}>
          <p className="banner danger">{error}</p>
          <button className="btn" onClick={() => location.reload()}>
            {s.common.retry}
          </button>
        </div>
      </div>
    );
  }
  if (!me) {
    return (
      <div className="center">
        <p style={{ color: 'var(--ink-dim)' }}>{s.common.loading}</p>
      </div>
    );
  }

  return (
    <div className="shell">
      <aside>
        <div className="brand">
          <span className="mark">S</span>
          <span>Squawk</span>
        </div>
        <nav>
          <NavLink to="/" end className={({ isActive }) => (isActive ? 'active' : '')}>
            {s.nav.overview}
          </NavLink>
          <NavLink to="/keys" className={({ isActive }) => (isActive ? 'active' : '')}>
            {s.nav.keys}
          </NavLink>
          <NavLink to="/usage" className={({ isActive }) => (isActive ? 'active' : '')}>
            {s.nav.usage}
          </NavLink>
        </nav>
        <div className="side-foot">
          <LocaleSwitch compact />
          <div className="email" style={{ marginTop: 10 }}>
            {me.email}
          </div>
          <button
            className="btn btn-sm"
            style={{ marginTop: 9, width: '100%' }}
            onClick={() => logout()}
          >
            {s.auth.signOut}
          </button>
        </div>
      </aside>
      <main>
        <Routes>
          <Route path="/" element={<Overview me={me} />} />
          <Route path="/keys" element={<Keys maxKeys={me.limits?.max_keys ?? 0} />} />
          <Route
            path="/usage"
            element={
              <Suspense
                fallback={<p style={{ color: 'var(--ink-dim)' }}>{s.common.loadingChart}</p>}
              >
                <Usage dailyQuota={me.limits?.requests_per_day ?? null} />
              </Suspense>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

export default function App() {
  return (
    <LocaleProvider>
      <Routes>
        <Route path="/callback" element={<Callback />} />
        <Route path="*" element={isLoggedIn() ? <Shell /> : <SignIn />} />
      </Routes>
    </LocaleProvider>
  );
}
